import parse from 'html-react-parser';
import Image, { StaticImageData } from 'next/image';

import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { Carousel } from 'ui';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  description: string;
  banners: StaticImageData[];
}

const InfoModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  description = '',
  banners
}: InfoModalProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: {
            serviceList: { infoModal }
          }
        }
      }
    }
  } = useTheme();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: '3xl', md: '3xl', lg: '3xl', '3xl': '6xl' }}
      isCentered
    >
      <ModalOverlay bg={infoModal.overlayBackground} opacity='0.1' />
      <ModalContent borderRadius='12px' overflow='hidden' margin='15px'>
        <ModalCloseButton
          display={{ base: 'flex', md: 'none' }}
          alignItems='center'
          justifyContent='center'
          w='40px'
          h='40px'
          zIndex='1'
          bg={infoModal.closeButtonBackground}
          color={infoModal.closeButtonColor}
        />
        <ModalBody p={0}>
          <Flex gap={0} flexDirection={{ base: 'column', md: 'row' }}>
            <Box
              display={{ base: 'block', md: 'none' }}
              w='100%'
              position='relative'
              height={{ base: '200px', sm: '300px', md: '400px' }}
              mb='28px'
            >
              <Image
                src={banners[0]}
                alt='modal banner'
                fill
                objectFit='cover'
              />
            </Box>

            <VStack
              align='start'
              spacing={{ base: '24px', md: '30px' }}
              flex='1'
              px={{ base: '30px', md: '40px', '2xl': '65px' }}
              pt={{ base: '0', md: '30px', lg: '40px', '2xl': '65px' }}
              pb={{ base: '24px', md: '40px' }}
            >
              <Text fontWeight='semibold' fontSize='18px'>
                {title}
              </Text>
              <Text fontWeight='semibold' fontSize='24px'>
                {subtitle}
              </Text>
              <Text fontSize='16px' lineHeight='160%'>
                {parse(description ?? '')}
              </Text>
              <Flex w='100%' justifyContent='center' mt='auto'>
                <Button
                  w='150px'
                  variant='solid'
                  colorScheme='black'
                  onClick={onClose}
                  fontWeight='bold'
                >
                  Close
                </Button>
              </Flex>
            </VStack>
            <Box
              display={{ base: 'none', md: 'block' }}
              maxW='345px'
              overflow='hidden'
              width='100%'
              marginRight='-3px'
              sx={{
                '& .slick-dots': {
                  bottom: '30px',
                  '& li': {
                    button: {
                      width: '10px',
                      height: '10px',
                      border: '1px solid',
                      borderColor: infoModal.bannerBorderColor,
                      borderRadius: '50%',
                      background: 'transparent',
                      _before: {
                        display: 'none'
                      }
                    },
                    '&.slick-active': {
                      button: { background: infoModal.activeButtonBackground }
                    }
                  }
                }
              }}
            >
              <Carousel
                banners={banners}
                settings={{
                  dots: true
                }}
              />
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default InfoModal;
